<section class="template-create-page">
  <header class="create-header">
    <div class="brand-block sr-only" aria-label="ExcelHire">
      <div class="brand-mark">EH</div>
      <p class="brand-name">ExcelHire</p>
    </div>

    <div class="header-left">
      <div class="header-title-wrap" [class.header-title-wrap--public]="isPublicTemplateConfigureMode">
        @if (!isPublicTemplateConfigureMode) {
          <button
            type="button"
            class="header-icon header-back-btn"
            (click)="goBackToTemplates()"
            [disabled]="publicTemplateConfigInitializing"
            aria-label="Back to templates"
            title="Back to templates"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="16" y1="10" x2="4" y2="10"></line>
              <polyline points="10 4 4 10 10 16"></polyline>
            </svg>
          </button>
        }
        <div class="title-wrap">
          <h1 class="page-heading-title">
            @if (isPublicTemplateConfigureMode) {
              Configure template fields
            } @else if (currentStep === 1) {
              {{ isEditMode ? 'Edit template' : 'Prepare template' }}
            } @else {
              Configure fields
            }
          </h1>
          <p class="sr-only">(Step {{ currentStep }}/2)</p>
        </div>
      </div>
    </div>

    <!-- Stepper Component (Step 1: Setup, Step 2: Place Fields) -->
    <div class="header-stepper" role="navigation" aria-label="Creation steps">
      <div class="step-item" [class.is-active]="currentStep === 1" [class.is-completed]="currentStep > 1">
        <span class="step-badge">1</span>
        <span class="step-label">Setup</span>
      </div>
      <div class="step-divider" [class.is-active]="currentStep > 1" aria-hidden="true"></div>
      <div class="step-item" [class.is-active]="currentStep === 2">
        <span class="step-badge">2</span>
        <span class="step-label">Place Fields</span>
      </div>
    </div>

    <div class="header-actions">
      @if (!isPublicTemplateConfigureMode && currentStep === 1) {
        <button type="button" class="primary" (click)="goToStepTwo()" [disabled]="loadingTemplateForEdit">Next</button>
      } @else {
        @if (stepTwoMode === 'configure') {
          <button type="button" class="primary ghost" (click)="setStepTwoMode('preview')" [disabled]="publicTemplateConfigInitializing">Preview</button>
        } @else {
          <button type="button" class="primary ghost" (click)="setStepTwoMode('configure')" [disabled]="publicTemplateConfigInitializing">Exit preview</button>
        }
        <button type="button" class="primary" (click)="saveTemplate()" [disabled]="savingTemplate || loadingTemplateForEdit || publicTemplateConfigInitializing">
          {{ submitButtonText }}
        </button>
      }
      @if (!isPublicTemplateConfigureMode) {
        <button type="button" class="header-icon close-btn" (click)="goBackToTemplates()" aria-label="Close">
          ✕
        </button>
      }
    </div>
  </header>

  <div class="wizard-body" [class.wizard-body--workspace]="currentStep === 2">
    @if (isPublicTemplateConfigureMode && publicTemplateSaveMessage) {
      <section class="floating-success-message" aria-live="polite" role="status">
        <p>{{ publicTemplateSaveMessage }}</p>
        <button
          type="button"
          class="floating-success-message__close"
          (click)="dismissPublicTemplateSaveMessage()"
          aria-label="Dismiss success message"
        >
          ×
        </button>
      </section>
    } @else if (publicTemplateSaveMessage) {
      <p class="helper-text">{{ publicTemplateSaveMessage }}</p>
    }

    @if (editLoadError) {
      <p class="field-error load-template-error">{{ editLoadError }}</p>
    }

    @if (publicTemplateConfigInitializing) {
      <section class="card loading-template-card">
        <p>Loading template fields…</p>
      </section>
    } @else if (loadingTemplateForEdit) {
      <section class="card loading-template-card">
        <p>Loading template for editing…</p>
      </section>
    } @else if (currentStep === 1) {
      <div class="step-container">
        <section class="card">
          <h2>Add template details</h2>

          <label for="templateTitle">Title*</label>
          <input
            id="templateTitle"
            type="text"
            [(ngModel)]="draft.templateTitle"
            placeholder="Enter template title"
          />
          @if (hasTemplateTitleError) {
            <p class="field-error">Template title is required.</p>
          }

          <label for="templateDescription">Description</label>
          <textarea
            id="templateDescription"
            rows="3"
            [(ngModel)]="draft.templateDescription"
            placeholder="Enter template description"
          ></textarea>
        </section>

        <section class="card">
          <h2>Add file(s)</h2>

          <input
            #fileInput
            type="file"
            class="hidden-file"
            accept="application/pdf,.pdf"
            (change)="onFileSelected($event)"
          />

          @if (!hasSelectedFile) {
            <div class="file-sources">
              <div class="source-column source-computer">
                <p class="source-title">Choose from computer</p>
                <button type="button" class="secondary" (click)="triggerChooseFile()">Browse</button>
                <p class="picker-sub">Preferred format: PDF</p>
              </div>
            </div>
          } @else {
            <div class="selected-file-row">
              <div class="file-summary">
                <span class="file-pill">PDF</span>
                <div>
                  <p class="file-name">{{ visibleFileName }}</p>
                  <p class="file-meta">File upload runs only when required during save.</p>
                </div>
              </div>
              <button type="button" class="danger" (click)="removeSelectedFile()">Remove</button>
            </div>
          }

          @if (fileError) {
            <p class="field-error">{{ fileError }}</p>
          }
          @if (hasFileError) {
            <p class="field-error">A PDF file is required.</p>
          }
        </section>

        <section class="card">
          <div class="role-header">
            <h2>Add role(s)</h2>
            <div class="signing-order-toggle-wrap">
              <label class="switch-toggle-label">
                <input type="checkbox" [(ngModel)]="draft.signingOrderEnabled" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">Signing order</span>
              </label>
              <span class="info-tooltip-icon" title="When enabled, roles receive document invitations sequentially.">
                <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="10" cy="10" r="8"></circle>
                  <line x1="10" y1="9" x2="10" y2="14"></line>
                  <line x1="10" y1="6" x2="10.01" y2="6"></line>
                </svg>
              </span>
            </div>
          </div>

          <div
            class="roles-wrap"
            cdkDropList
            [cdkDropListData]="draft.roles"
            [cdkDropListDisabled]="!draft.signingOrderEnabled"
            (cdkDropListDropped)="dropRole($event)"
          >
            @for (role of draft.roles; track role.id; let i = $index) {
              <article class="role-card" cdkDrag [cdkDragDisabled]="!draft.signingOrderEnabled">
                <div class="role-row-top">
                  <div class="role-index-wrap">
                    @if (draft.signingOrderEnabled) {
                      <button type="button" class="drag-handle" cdkDragHandle aria-label="Drag role" title="Drag role">
                        ⋮⋮
                      </button>
                    }
                    <div class="role-number">{{ i + 1 }}</div>
                  </div>

                  <input
                    type="text"
                    [(ngModel)]="role.role"
                    placeholder="Role*"
                    [class.invalid]="!!roleError(role)"
                  />

                  <div class="role-inline-actions">
                    <button
                      type="button"
                      class="icon-small remove-role-btn"
                      (click)="removeRole(role.id)"
                      aria-label="Remove role"
                      title="Remove role"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>

                @if (roleError(role)) {
                  <p class="field-error">{{ roleError(role) }}</p>
                }

                <div class="role-row-bottom">
                  <input
                    type="text"
                    [(ngModel)]="role.defaultRecipientName"
                    placeholder="Default recipient name (optional)"
                  />
                  <input
                    type="email"
                    [(ngModel)]="role.defaultRecipientEmail"
                    placeholder="Default recipient email (optional)"
                  />
                </div>
              </article>
            }
          </div>

          <button type="button" class="role-add" (click)="addRole()">+ Add Role</button>
        </section>

        <section class="card">
          <h2>Add CC</h2>
          <textarea
            rows="2"
            [(ngModel)]="draft.cc"
            placeholder="Enter one or more email addresses separated by a comma."
          ></textarea>
        </section>

        <section class="card">
          <h2>Document</h2>

          <label for="documentTitle">Title*</label>
          <input
            id="documentTitle"
            type="text"
            [(ngModel)]="draft.documentTitle"
            placeholder="Enter document title"
          />
          @if (hasDocumentTitleError) {
            <p class="field-error">Document title is required.</p>
          }

          <label for="documentMessage">Message</label>
          <textarea
            id="documentMessage"
            rows="3"
            [(ngModel)]="draft.documentMessage"
            placeholder="Enter message for all recipients"
          ></textarea>
        </section>

        <section class="card">
          <h2>Template Settings</h2>

          <label for="templateTags">Tags</label>
          <textarea
            id="templateTags"
            rows="2"
            [(ngModel)]="draft.tags"
            placeholder="Enter one or more tags separated by a comma."
          ></textarea>
        </section>

        <section class="card">
          <h2>Template Usage Settings</h2>

          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.usageSettings.allowAddMoreFiles" />
            <span>Allow sender to add more files</span>
          </label>
          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.usageSettings.allowReplaceRearrangeDeleteFiles" />
            <span>Allow sender to replace, rearrange and delete files</span>
          </label>
          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.usageSettings.allowAddMoreRecipients" />
            <span>Allow sender to add more recipients</span>
          </label>
          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.usageSettings.allowEditDocumentMessage" />
            <span>Allow sender to edit the document message before sending</span>
          </label>
          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.usageSettings.allowAddNewFields" />
            <span>Allow senders to add new fields.</span>
          </label>
          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.usageSettings.allowEditOrDeleteFields" />
            <span>Allow senders to edit or delete fields.</span>
          </label>
        </section>

        <section class="card">
          <h2>Document settings</h2>

          <label for="brandSelect">Select Brand</label>
          <select
            id="brandSelect"
            [ngModel]="draft.documentSettings.brandId ?? ''"
            (ngModelChange)="onBrandSelectionChange($event)"
            [disabled]="brandsLoading"
          >
            <option value="">Default brand</option>
            @for (brand of brands; track brand.brandId) {
              <option [value]="brand.brandId">{{ brand.name }}</option>
            }
          </select>

          <label class="check-row">
            <input type="checkbox" [(ngModel)]="draft.documentSettings.allowPrintAndSign" />
            <span>Allow signers to print and sign</span>
          </label>
        </section>
      </div>
    } @else {
      <app-field-configurator-workspace
        #viewerHost
        [mode]="stepTwoMode"
        [actors]="fieldConfiguratorActors"
        [activeActorId]="activeRoleId"
        actorSelectLabel="Signer role"
        [fieldPalette]="fieldPalette"
        [canPlaceFields]="canPlaceFields"
        [stepTwoError]="stepTwoError"
        [showWalkthroughButton]="true"
        [settingsTitle]="selectedFieldSettingsTitle"
        [selectedField]="selectedField"
        [settingsPanelOptions]="fieldConfiguratorSettingsPanelOptions"
        [optionSets]="fieldConfiguratorOptionSets"
        [positionX]="selectedFieldPositionPx('x')"
        [positionY]="selectedFieldPositionPx('y')"
        [checkboxGroupName]="checkboxGroupNameModel()"
        [checkboxGroupValues]="checkboxGroupValuesModel()"
        [checkboxGroupDefaultValues]="checkboxGroupDefaultValuesModel()"
        [radioGroupName]="radioGroupNameModel()"
        [radioGroupValues]="radioGroupValuesModel()"
        [radioGroupDefaultValue]="radioGroupDefaultValueModel()"
        [previewFilter]="previewRecipientFilter"
        [previewMeta]="fieldConfiguratorPreviewMeta"
        [fields]="stepTwoMode === 'preview' ? draft.fields : []"
        [viewerPdfSource]="viewerPdfSource"
        [hasPendingSource]="!!draft.selectedFile"
        [emptyNoFileMessage]="'No file selected. Go back to Step 1 and add a PDF.'"
        [emptyPreparingMessage]="'Preparing PDF preview...'"
        [pdfLoadError]="pdfLoadError"
        [pdfLoadErrorDetail]="pdfLoadErrorDetail"
        [pdfBootstrapError]="pdfBootstrapError"
        [showViewerInstance]="showViewerInstance"
        [viewerRenderNonce]="viewerRenderNonce"
        [viewerPageBinding]="viewerPageBinding"
        [pdfLoading]="pdfLoading"
        [pageCount]="pageCount"
        [retryButtonLabel]="'Retry preview'"
        [showStepOneButton]="!isPublicTemplateConfigureMode"
        [stepOneButtonLabel]="'Go to Step 1'"
        (activeActorIdChange)="activeRoleId = $event"
        (paletteDragStart)="onPaletteDragStart($event.event, $event.type)"
        (paletteDragEnd)="onPaletteDragEnd()"
        (paletteTileClick)="onPaletteTileClick($event)"
        (walkthroughRequest)="restartWalkthrough()"
        (goToStepOne)="goToStepOneForPdfReselect()"
        (retryPreview)="retryPdfPreview()"
        (pdfLoadingStarts)="onPdfLoadingStarts()"
        (pdfLoaded)="onPdfLoaded()"
        (pdfLoadingFailed)="onPdfLoadingFailed($event)"
        (pagesLoaded)="onPagesLoaded($event)"
        (pageRendered)="onPageRendered($event)"
        (pageChange)="onPageChanged($event)"
        (settingsAction)="handleFieldConfiguratorSettingsAction($event)"
        (previewFilterChange)="previewRecipientFilter = $event; onPreviewRecipientFilterChanged()"
        (previewMetaChange)="handleFieldConfiguratorPreviewMetaChange($event)"
      >
        @if (enablePdfEventTrace) {
          <aside canvas-debug class="pdf-debug-panel" aria-label="PDF debug trace">
            <p class="pdf-debug-state">
              attempt=#{{ pdfLoadAttemptId }} · events={{ pdfEventCountThisAttempt }} · retries={{ pdfBootstrapRetryCount }} · nonce={{ viewerRenderNonce }} · loading={{ pdfLoading }} · loaded={{ pdfLoaded }} · mounted={{ viewerMounted }} · pages={{ pageCount }} · active={{ activePage }}
            </p>
            <div class="pdf-debug-log">
              @if (pdfTracePanelEntries.length === 0) {
                <p class="pdf-debug-empty">No PDF trace events yet.</p>
              } @else {
                @for (entry of pdfTracePanelEntries; track entry.id) {
                  <p class="pdf-debug-row">
                    {{ entry.timestampIso }} · #{{ entry.attemptId }} · {{ entry.event }} · source={{ entry.sourceKind }}
                    @if (entry.payloadText) {
                      · payload={{ entry.payloadText }}
                    }
                  </p>
                }
              }
            </div>
          </aside>
        }
        @if (walkthroughVisible && stepTwoMode === 'configure') {
          <div workspace-overlay class="walkthrough-mask">
            <article class="walkthrough-card" [class.right]="currentWalkthroughStep.target === 'right'">
              <h3>{{ currentWalkthroughStep.title }}</h3>
              <p>{{ currentWalkthroughStep.description }}</p>
              <footer>
                <span>{{ currentWalkthroughStep.progressLabel }}</span>
                <div class="walkthrough-actions">
                  <button type="button" class="ghost-link" (click)="skipWalkthrough()">Skip</button>
                  <button type="button" class="next-btn" (click)="nextWalkthroughStep()">
                    {{ walkthroughIndex === walkthroughSteps.length - 1 ? 'Done' : 'Next' }}
                  </button>
                </div>
              </footer>
            </article>
          </div>
        }
      </app-field-configurator-workspace>
    }
  </div>

  @if (showSaveSuccessModal) {
    <div class="save-modal-mask">
      <section class="save-modal">
        <div class="save-modal-body">
          <div class="success-icon">✓</div>
          <p>{{ saveSuccessText }}</p>
        </div>
        <footer class="save-modal-actions">
          <button type="button" class="secondary-light" (click)="closeSavedModal()">Not now</button>
          <button type="button" class="primary" (click)="closeSavedModal()">Yes, use template</button>
        </footer>
      </section>
    </div>
  }

  @if (currentStep === 1 && !isPublicTemplateConfigureMode) {
    <div class="fixed-bottom-bar">
      <div class="fixed-bottom-inner">
        <div class="bottom-actions-group">
          <button type="button" class="btn-cancel" (click)="goBackToTemplates()">Cancel</button>
          <button type="button" class="btn-next" (click)="goToStepTwo()" [disabled]="loadingTemplateForEdit">
            Next: Place Fields →
          </button>
        </div>
      </div>
    </div>
  }
</section>
